import React from 'react';
import { servicesData } from './ServicesData';
import { ChevronRight } from 'lucide-react';

// Brand colors specifically tailored for a pure white theme
const brandColors = {
  bgWhite: "#FFFFFF",
  blackPrimary: "#1A1A1A", // Deep black for primary text/headings
  textGray: "#4A4A4A",     // Dark gray for paragraph readability
  crimson: "#A10F1A",      // Signature red
  gold: "#C79A3B",         // Signature gold
};

const ServicesList = () => {
  return (
    <section 
      className="py-24 px-6 md:px-12 relative" 
      style={{ backgroundColor: brandColors.bgWhite }}
    >
      {/* Faint background watermark for white background */}
      <div
        className="absolute top-10 right-10 pointer-events-none select-none z-0"
        style={{
          fontFamily: "'Cinzel', serif",
          fontSize: "150px",
          fontWeight: 700,
          color: "rgba(26, 26, 26, 0.03)", 
          lineHeight: 1,
          textTransform: "uppercase",
        }}
      >
        LAW
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* SECTION HEADER */}
        <div className="mb-16 text-center md:text-left flex flex-col md:items-start items-center">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-4">
            <span
              style={{
                width: "32px",
                height: "1px",
                background: `linear-gradient(270deg, ${brandColors.crimson}, transparent)`,
              }}
              className="md:hidden block"
            />
            <span
              style={{
                fontFamily: "'Cinzel', serif",
                fontSize: "11px",
                letterSpacing: "0.25em",
                color: brandColors.crimson, 
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              Our Expertise
            </span>
            <span
              style={{
                width: "48px",
                height: "1px",
                background: `linear-gradient(90deg, ${brandColors.crimson}, transparent)`,
              }}
            />
          </div>

          <h2 
            style={{ 
              fontFamily: "'Cinzel', serif",
              color: brandColors.blackPrimary 
            }} 
            className="text-4xl md:text-5xl font-bold leading-tight tracking-wider uppercase"
          >
            Legal <span style={{ color: brandColors.gold, fontWeight: 400 }}>Practices</span>
          </h2>
          
          <div className="mt-5 flex items-center gap-3">
            <div style={{ width: "6px", height: "6px", border: `1px solid ${brandColors.gold}`, transform: "rotate(45deg)" }} />
            <div style={{ width: "100px", height: "0.5px", background: `linear-gradient(90deg, ${brandColors.gold}, transparent)` }} />
          </div>
        </div>

        {/* SERVICES GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {servicesData.map((service, index) => (
            <div 
              key={service.id} 
              // Changed padding from p-10 to p-7 for a more compact card
              className="group relative p-7 transition-all duration-500 hover:-translate-y-2 flex flex-col justify-between overflow-hidden bg-white shadow-md hover:shadow-2xl"
              style={{ 
                border: "1px solid rgba(26, 26, 26, 0.08)" 
              }}
            >
              {/* Top Animated Gradient Border */}
              <div 
                className="absolute top-0 left-0 h-[3px] w-0 group-hover:w-full transition-all duration-700"
                style={{ background: `linear-gradient(90deg, ${brandColors.crimson}, ${brandColors.gold})` }}
              />

              {/* Content Area */}
              <div className="relative z-10">
                {/* Visual Icon - Reduced size and margin */}
                <div 
                  className="text-4xl mb-5 transform transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6 block"
                  style={{ color: brandColors.gold }}
                >
                  {service.icon}
                </div>

                {/* Title - Reduced text size from text-2xl to text-xl */}
                <h3 
                  style={{ fontFamily: "'Cinzel', serif", color: brandColors.blackPrimary }}
                  className="text-xl font-bold mb-3 tracking-wide group-hover:text-[#A10F1A] transition-colors uppercase"
                >
                  {service.title}
                </h3>

                {/* Description - Reduced font size and margin */}
                <p 
                  style={{ 
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                    color: brandColors.textGray 
                  }}
                  className="text-[16px] leading-relaxed mb-6 font-medium transition-colors"
                >
                  {service.shortDesc}
                </p>
              </div>

              {/* Action Link */}
              <a 
                href={`/services/${service.id}`}
                className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] transition-all relative z-10 w-max"
                style={{ color: brandColors.blackPrimary }}
              >
                <span className="group-hover:text-[#A10F1A] transition-colors">Read More</span>
                <div 
                  className="flex items-center justify-center w-7 h-7 rounded-full transition-all duration-300 group-hover:border-[#A10F1A] group-hover:bg-[#A10F1A]"
                  style={{ border: `1px solid rgba(26,26,26,0.2)` }}
                >
                  <ChevronRight size={12} className="group-hover:text-white group-hover:translate-x-0.5 transition-all" />
                </div>
              </a>

              {/* Background Number Detail - Reduced size from text-9xl to text-8xl */}
              <span 
                style={{ fontFamily: "'Cinzel', serif", color: "rgba(199, 154, 59, 0.08)" }}
                className="absolute -bottom-4 -right-2 text-8xl font-black select-none pointer-events-none transition-all duration-500 group-hover:scale-110"
              >
                0{index + 1}
              </span>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default ServicesList;